import { Box, useTheme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { serializeDate } from "@notemeal/shared/ui/utils/dateTimes";
import CalendarMonth, { CalendarMonthRendDayProps } from "apps/web/src/components/Calendar/Month";
import { addWeeks, endOfMonth, startOfMonth } from "date-fns";
import React, { Dispatch } from "react";
import { MealPlanDateAssignmentAction } from "../../../MealPlans/Content/Calendar/Form/reducer";
import { MealPlanDateAssignment } from "../../../MealPlans/Content/Calendar/Form/types";
import { getMealPlanAssignedDatesInRange } from "../../../MealPlans/Content/Calendar/Form/utils";
import { useMealPlanCalendarContext } from "../../../MealPlans/contexts/Calendar";

const useStyles = makeStyles(() =>
  createStyles({
    day: {
      position: "relative",
    },
    baseDot: {
      position: "absolute",
      height: 6,
      width: 6,
      borderRadius: "50%",
      bottom: 2,
    },
    singleDot: {
      left: "calc(50% - 3px)",
    },
    doubleDot1: {
      left: "calc(50% - 7.5px)",
    },
    doubleDot2: {
      left: "calc(50% + 1.5px)",
    },
  })
);

interface GoalDateAssignmentCalendarProps {
  state: MealPlanDateAssignment;
  dispatch: Dispatch<MealPlanDateAssignmentAction>;
  onClickCalendarDate: (date: string) => void;
}

export const GoalDateAssignmentCalendar = ({ state, dispatch, onClickCalendarDate }: GoalDateAssignmentCalendarProps) => {
  const classes = useStyles();
  const { startOfMonthDate, onChangeStartOfMonthDate } = useMealPlanCalendarContext();
  // Add / subtract extra week to handle days not part of the month still shown on calendar
  const startDate = serializeDate(addWeeks(startOfMonth(startOfMonthDate), -1));
  const endDate = serializeDate(addWeeks(endOfMonth(startOfMonthDate), 1));

  const {
    palette: { info },
  } = useTheme();

  const assignedDates = getMealPlanAssignedDatesInRange(state, startDate, endDate);

  const renderDay = (props: PickersDayProps<Date> & CalendarMonthRendDayProps) => {
    const { isoDate, dayText, isCurrentMonth, ...other } = props;
    const selected = assignedDates.includes(isoDate);
    const backgroundColor = selected ? info.main : undefined;
    const disabled = isoDate < serializeDate(new Date());
    return (
      <Box
        className={classes.day}
        onClick={() => {
          dispatch({
            type: "ToggleIndividualDateAssignment",
            payload: {
              date: isoDate,
            },
          });
          onClickCalendarDate(isoDate);
        }}
      >
        <PickersDay
          {...other}
          disabled={disabled}
          selected={false}
          sx={{
            backgroundColor,
            ":hover": {
              backgroundColor,
            },
            ":focus": {
              backgroundColor,
            },
          }}
        >
          {dayText}
        </PickersDay>
      </Box>
    );
  };

  return <CalendarMonth
    renderDay={renderDay}
    calendarDate={startOfMonthDate}
    onChangeCalendarDate={onChangeStartOfMonthDate} />;
};
